import type { ApolloClient } from '@apollo/client';
import {
  type RentalComparable,
  toRentalComparable,
} from '@realadvisor/listings/rental-cma';

import type { RentalCmaCohortsQuery } from '@graphql/__generated__/graphql';
import { composePropertyType } from '@modules/_shared/utils/propertyTypes';
import { RENTAL_CMA_COHORTS } from '@modules/reports/cma-reports/rentalQueries';

type RentalSearchProperty =
  | {
      lat?: number | null;
      lng?: number | null;
      computed_surface?: number | null;
      number_of_rooms?: number | null;
      number_of_bedrooms?: number | null;
      property_type?: {
        name?: string | null;
        main_type?: string | null;
      } | null;
      latest_appraisal?: {
        rent_value?: number | null;
      } | null;
    }
  | null
  | undefined;

export const getRentalCountryFlags = (countryCode: string) => ({
  ch: countryCode === 'CH',
  es: countryCode === 'ES',
  fr: countryCode === 'FR',
  it: countryCode === 'IT',
  pl: countryCode === 'PL',
  pt: countryCode === 'PT',
});

export const getRentalCountryVariables = <T>(
  countryCode: string,
  where: T,
) => ({
  ...getRentalCountryFlags(countryCode),
  chWhere: where,
  esWhere: where,
  frWhere: where,
  itWhere: where,
  plWhere: where,
  ptWhere: where,
});

type RentalCountryRowKey =
  | 'ch_cma_rental_listings'
  | 'es_cma_rental_listings'
  | 'fr_cma_rental_listings'
  | 'it_cma_rental_listings'
  | 'pl_cma_rental_listings'
  | 'pt_cma_rental_listings';

type RentalCountryRowsData = Partial<
  Record<RentalCountryRowKey, readonly unknown[]>
>;

export const getRentalCountryRows = <Data extends RentalCountryRowsData>(
  data: Data | null | undefined,
) =>
  (data?.ch_cma_rental_listings ??
    data?.es_cma_rental_listings ??
    data?.fr_cma_rental_listings ??
    data?.it_cma_rental_listings ??
    data?.pl_cma_rental_listings ??
    data?.pt_cma_rental_listings ??
    []) as NonNullable<Data[Extract<keyof Data, RentalCountryRowKey>]>;

export const getRentalCohortsSearch = (
  property: RentalSearchProperty,
  countryCode: string,
) => {
  const rooms = property?.number_of_rooms ?? property?.number_of_bedrooms;

  return {
    variables: {
      lat: property?.lat ?? 0,
      lng: property?.lng ?? 0,
      property_main_type: composePropertyType(
        property?.property_type?.main_type ?? null,
        property?.property_type?.name ?? null,
      ),
      computed_surface: property?.computed_surface ?? null,
      number_of_rooms: rooms != null && rooms > 0 ? rooms : null,
      rent_net_monthly: property?.latest_appraisal?.rent_value ?? null,
      ...getRentalCountryFlags(countryCode),
    },
  };
};

export const getRentalCohortListings = (
  data: RentalCmaCohortsQuery | null | undefined,
) =>
  (
    data?.ch_get_similar_rental_listings_cohorts ??
    data?.es_get_similar_rental_listings_cohorts ??
    data?.fr_get_similar_rental_listings_cohorts ??
    data?.it_get_similar_rental_listings_cohorts ??
    data?.pl_get_similar_rental_listings_cohorts ??
    data?.pt_get_similar_rental_listings_cohorts ??
    []
  ).flatMap(({ listing }) => (listing ? [listing] : []));

export const getRentalCohortComparables = (
  listings: ReturnType<typeof getRentalCohortListings>,
): RentalComparable[] =>
  listings.flatMap(listing => {
    const comparable = toRentalComparable(listing);
    return comparable ? [comparable] : [];
  });

export const fetchRentalComparables = async (
  client: ApolloClient,
  variables: ReturnType<typeof getRentalCohortsSearch>['variables'],
): Promise<RentalComparable[]> => {
  const result = await client.query({
    query: RENTAL_CMA_COHORTS,
    variables,
    context: { clientName: 'scrapers' },
    fetchPolicy: 'network-only',
  });
  if (result.error) {
    throw result.error;
  }
  return getRentalCohortComparables(getRentalCohortListings(result.data));
};
